import { useEffect, useState } from "react";
import { useAccount } from "wagmi";
import Loader from "~/core/components/Loader";
import { api } from "../api";
import { icons } from "../assets/icons/icons";
import { useSound } from "../components/SoundHandler";
import { ContainerShape, HexagonShape, InnerShape, InnerShape2 } from "../components/Shape";
import { useReferrer } from "../hooks/useReferral";
import { useError } from "~/core/contexts/ErrorContext";
import { useToast } from "~/core/contexts/ToastContext";
import { blinkoReferralCodeStorage } from "~/core/storages";

export const NoReferral = ({ onDismiss }: { onDismiss: () => void }) => {
  const sounds = useSound()
  const toast = useToast()
  const { handleError } = useError()
  const { address } = useAccount()
  const { mutate: mutateReferrer } = useReferrer()

  const [referralCode, setReferralCode] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    const code = blinkoReferralCodeStorage.get()
    if (code) {
      setReferralCode(code)
      toast.success('Referral code auto-filled')
    }
  }, [])

  const onClose = () => {
    onDismiss()
    sounds.play('click')
  };

  const onConfirm = async () => {
    if (isLoading) return
    const trimmed = referralCode.trim()
    if (!trimmed) {
      toast.error('Please enter a referral code')
      return
    }
    sounds.play('click')
    setIsLoading(true)
    try {
      await api.applyReferralCode(address!, trimmed)
      await mutateReferrer()
      toast.success('Referral code applied')
      onDismiss()
    } catch (error) {
      handleError('Failed to apply referral code', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-[#18181B]/50">
      <div>
        <ContainerShape {...{cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 }}>
          <div className="p-6 w-[350px] md:w-[448px]">
            <div className="flex flex-row justify-between">
              <div className="mt-1 text-2xl font-blinko font-black text-white">
                NO REFERRAL YET
              </div>

                <button className="" onClick={onClose}>
                <HexagonShape cornerRadius={6} strokeWidth={1}>
                  <div className="flex justify-center items-center p-2">
                    <img src={icons.close} className='w-6 h-6' />
                  </div>
                </HexagonShape>
              </button>
              
            </div>
            <p className="inline-flex gap-0.5 text-sm text-white/70 mt-3">
              <span>You cannot collect</span> <span className="inline-flex gap-0.5"><img src={icons.jars} className="w-3" /><span className="text-white font-blinko text-[10px]">honey</span></span> <span>without entering one.</span>
            </p>

            <div className='mt-3'>
              <InnerShape2 cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                <input
                  className="flex-1 w-full h-10 pl-3 pr-0 py-3 bg-transparent text-sm text-white"
                  value={referralCode}
                  placeholder="HONEY123"
                  onChange={(e) => setReferralCode(e.target.value)}
                  disabled={isLoading}
                />
              </InnerShape2>
            </div>

            <div className="mt-6 flex justify-end space-x-2">
              <a href="https://x.com/search?q=%22blinko%22%20(%22ref%22%20OR%20%22referral%22)&src=typed_query&f=live" target="_blank" rel="noopener noreferrer">
                <div className="relative px-3 py-3">
                  <p className="text-sm font-blinko text-white">
                    Find Referral
                  </p>
                </div>
              </a>

              <button onClick={onConfirm} disabled={isLoading}>
                <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                  <div className="relative px-6 py-3">
                    <p 
                      className="text-sm font-blinko text-white"
                      style={{ opacity: isLoading ? 0 : 1 }}
                    >
                      Apply Code
                    </p>
                    {isLoading && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <Loader stroke="white" size="16px" />
                      </div>
                    )}
                  </div>
                </InnerShape>
              </button>
            </div>
          </div>
        </ContainerShape>
      </div>
    </div>
  );
}
